/**
 * NaraDoor API client for NaraHub.
 * Firebase callable functions client for NaraMachine services.
 */

import { callApp, callAuth, type AppMethod } from "../lib/appProxy";
import { callable } from "../lib/callable";
import { getCurrentWorkspaceId } from "../lib/workspaceStore";
import type { RecordSchema } from "../lib/productRecords";

// Custom error class for insufficient boosts (HTTP 402 from backend)
export class InsufficientBoostsError extends Error {
  public readonly required?: number;
  public readonly balance?: number;

  constructor(message: string, details?: { required?: number; balance?: number }) {
    super(message);
    this.name = "InsufficientBoostsError";
    this.required = details?.required;
    this.balance = details?.balance;
  }
}

// Generic callable function wrapper with error handling
// Automatically includes workspaceId from the workspace store
async function callFunction<T extends Record<string, unknown>, R>(
  name: string,
  data?: T
): Promise<R> {
  try {
    // Automatically inject workspaceId if not already provided
    const workspaceId = getCurrentWorkspaceId();

    const payload = {
      ...data,
      // Only add workspaceId if available and not explicitly provided in data
      ...(workspaceId && !(data && "workspaceId" in data) && { workspaceId }),
    } as T;

    const result = await callable<T, R>(name)(payload);
    return result.data;
  } catch (error: unknown) {
    throw callError(error);
  }
}

// An id as one path segment.
const seg = encodeURIComponent;

// An appProxy /app route (T-330): callApp sends the workspace header (the current workspace unless
// one is given) and refreshes the cache after a write; errors read as a callable's.
async function callRoute<R>(method: AppMethod, path: string, body?: unknown, workspaceId?: string): Promise<R> {
  return withCallError(callApp<R>(method, path, body, workspaceId));
}

// An authProxy /auth route (T-330 batch 8): the web's identity-writer calls, run as hub-auth.
async function callAuthRoute<R>(method: "GET" | "POST", path: string, body?: unknown): Promise<R> {
  return withCallError(callAuth<R>(method, path, body));
}

async function withCallError<R>(call: Promise<R>): Promise<R> {
  try {
    return await call;
  } catch (error: unknown) {
    throw callError(error);
  }
}

/** The error a page reads from a failed call: `<code>: <message>`, or InsufficientBoostsError. */
function callError(error: unknown): unknown {
  // Re-throw with better error message, preserving details for structured errors
  if (error && typeof error === "object" && "code" in error && "message" in error) {
    const fbError = error as { code: string; message: string; details?: unknown };

    // Detect insufficient boosts errors (HTTP 402 from backend)
    // Accept both legacy "insufficient_boosts" and new "awaiting_boosts" reason codes
    const errorMsg = fbError.message || "";
    const details = fbError.details as Record<string, unknown> | undefined;
    if (
      errorMsg.includes("insufficient_boosts") ||
      errorMsg.includes("awaiting_boosts") ||
      (details && (details.reason === "insufficient_boosts" || details.reason === "awaiting_boosts"))
    ) {
      return new InsufficientBoostsError(
        "Insufficient boosts to process this request",
        {
          required: typeof details?.required === "number" ? details.required : undefined,
          balance: typeof details?.balance === "number" ? details.balance : undefined,
        }
      );
    }

    const err = new Error(`${fbError.code}: ${fbError.message}`);
    if (fbError.details) {
      (err as Error & { details: unknown }).details = fbError.details;
    }
    return err;
  }
  return error;
}

// =====================================================
// Types
// =====================================================

export interface Workspace {
  id: string;
  name: string;
  handle?: string;
  instructions?: string;
  settings?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

export interface WorkspacesResponse {
  workspaces: Workspace[];
  total?: number;
  /** The server gave a caller with no workspace their own: refresh the ID token. */
  workspaceBootstrapped?: boolean;
}

export type WorkspaceCollaboratorRole = "owner" | "admin" | "editor" | "viewer" | "uploader" | "inactive";

export interface WorkspaceCollaborator {
  id: string;
  user_id: string;
  workspace_id: string;
  role: WorkspaceCollaboratorRole;
  permissions?: string[] | null;
  effective_permissions?: string[];
  status?: string;
  created_at?: string;
  updated_at?: string;
  invited_at?: string;
  invited_by_user_id?: string;
  invite_accepted_at?: string;
  is_on_any_project?: boolean;
  /** "agent" for an AI agent member (T-358); nara-core marks it on the row or on its user. */
  kind?: "human" | "agent";
  user?: {
    id: string;
    kind?: "human" | "agent";
    email: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  };
}

export interface WorkspaceCollaboratorsResponse {
  collaborators: WorkspaceCollaborator[];
  total?: number;
}

/** A workspace admin's private label on a seat (T-192). Admin-only: never part of a collaborator response. */
export interface MemberLabel {
  user_id: string;
  label_name: string | null;
  label_phone: string | null;
  label_note: string | null;
}

export type MemberLabelFields = Omit<MemberLabel, "user_id">;

export interface Tag {
  id: string;
  workspace_id: string;
  name: string;
  description?: string;
  color?: string;
  project_count: number;
  document_count: number;
  file_count: number;
  created_at: string;
  updated_at: string;
}

export interface TagSummary {
  id: string;
  name: string;
  color?: string;
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  workspace_id: string;
  instructions?: string;
  template_id?: string;
  // T-370: template_id, else the workspace default, else the platform default (set by the server).
  effective_template_id?: string;
  tags?: TagSummary[];
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  pinned?: boolean;
  document_count?: number;
  collaborators_count?: number;
}

export interface ProjectsResponse {
  projects: Project[];
  total?: number;
}

export interface Collaborator {
  id: string;
  user_id: string;
  project_id: string;
  email_transcription?: boolean;
  user?: {
    id: string;
    email?: string;
    phone?: string;
    first_name?: string;
    last_name?: string;
  };
}

export interface CollaboratorsResponse {
  collaborators: Collaborator[];
  total?: number;
}

export interface Document {
  id: string;
  name: string;
  project_id: string;
  workspace_id?: string;
  status?: string;
  document_type?: string;
  vertex_index_status?: string;
  file_type?: string;
  file_size?: number;
  storage_path?: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  failure_reason?: string | null;
  workflow_started_at?: string | null;
}

export interface DocumentsResponse {
  documents: Document[];
  total?: number;
}

export interface DocumentFile {
  id: string;
  filename?: string;
  name?: string;
  url?: string;
  mime_type?: string;
  mimeType?: string;
  size?: number;
  storage_key?: string;
  metadata?: Record<string, unknown>;
}

export interface DocumentView {
  id: string;
  name: string;
  status?: string;
  content_json?: unknown;
  content_md?: string;
  // Legacy format
  files?: DocumentFile[];
  // NaraMachine format
  audio_file?: DocumentFile;
  image_files?: DocumentFile[];
  document_files?: DocumentFile[];
}

export interface DocumentVersionSummary {
  id: string;
  document_id: string;
  label?: string;
  created_by?: string;
  created_at: string;
}

export interface DocumentVersionDetail extends DocumentVersionSummary {
  content_json: unknown;
}

export interface VertexIndexStatusProject {
  project_id: string;
  project_name: string;
  total_documents: number;
  pending_count: number;
  no_content_count: number;
  failed_count: number;
  indexed_count: number;
}

export interface VertexIndexStatusResponse {
  total_pending: number;
  total_no_content: number;
  total_failed: number;
  total_indexed: number;
  projects: VertexIndexStatusProject[];
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description?: string;
  scope: "global" | "workspace";
  workspace_id?: string;
  workspace_name?: string;
  is_default?: boolean;
  workspace_ids?: string[];
  document_recipe_ids?: string[];
  default_transcription_recipe_id?: string;
  default_project_recipe_id?: string;
  default_extraction_recipe_id?: string;
  is_workspace_shared?: boolean;
  created_at?: string;
}

export interface ProjectTemplatesResponse {
  templates: ProjectTemplate[];
  total?: number;
}

export interface ShapeRecipe {
  id: string;
  name: string;
  input_mode?: string;
  description?: string;
  code?: string;
  prompt?: string;
  template_html?: string;
  locale?: string;
  schema?: Record<string, unknown>;
  created_at?: string;
  active_version?: number | null;
  has_draft_changes?: boolean;
  status?: string; // "draft" | "published" | "published_with_draft"
}

export interface ShapeRecipesResponse {
  recipes: ShapeRecipe[];
}

export interface ShapeRecipeVersion {
  version: number;
  code?: string;
  prompt?: string;
  template_html?: string;
  schema?: Record<string, unknown>;
  locale?: string;
  published_at: string;
  is_active: boolean;
}

export interface ShapeRecipeVersionsResponse {
  recipe_id: string;
  versions: ShapeRecipeVersion[];
  count: number;
}

// Document Structure Extraction types
export interface DocumentDefinitionField {
  name: string;
  type: string;
  description: string;
}

export interface DocumentDefinitionSection {
  name: string;
  level: number;
  expected_content: string;
  repeatable: boolean;
  fields: DocumentDefinitionField[];
  subsections?: DocumentDefinitionSection[];
}

export interface DocumentDefinition {
  document_type: string;
  description: string;
  sections: DocumentDefinitionSection[];
}

export interface DocumentStructureResponse {
  status: string;
  document_id: string;
  file_id: string;
  document_definition?: DocumentDefinition;
  processing_time_ms?: number;
}

export interface WorkspaceSettings {
  id: string;
  workspace_id: string;
  interface_id: string;
  preferences: Record<string, unknown>;
}

// UnifiedCollaborator uses the same shape as WorkspaceCollaborator
export type UnifiedCollaborator = WorkspaceCollaborator;

export interface UnifiedCollaboratorsResponse {
  collaborators: UnifiedCollaborator[];
  total?: number;
}

export interface CollaboratorProjectAssociation {
  project_id: string;
  project_name: string;
  email_transcription: boolean;
  created_at?: string;
}

export interface CollaboratorDetailResponse {
  user_id: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  workspace_role?: WorkspaceCollaboratorRole | null;
  workspace_collaborator_status?: "active" | "invited" | "deactivated";
  is_on_any_project?: boolean;
  /** Set only for an AI agent. */
  kind?: "agent";
  projects: CollaboratorProjectAssociation[];
}

// =====================================================
// User Profile
// =====================================================

export interface UserProfile {
  id: string;
  email: string;
  phone: string | null;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  locale: string;
  timezone: string;
  isAdmin: boolean;
}

export async function getUserProfile(): Promise<UserProfile> {
  return callAuthRoute("GET", "/profile");
}

export async function updateUserProfile(data: {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}): Promise<UserProfile> {
  return callAuthRoute("POST", "/profile", data);
}

export async function updateUserTimezone(timezone: string): Promise<UserProfile> {
  return callAuthRoute("POST", "/profile-timezone", { timezone });
}

export async function updateUserLocale(locale: string): Promise<UserProfile> {
  return callAuthRoute("POST", "/profile-locale", { locale });
}

// =====================================================
// Workspaces
// =====================================================

/** `skipBootstrap`: a caller with no workspace gets none (the invite page — the invite is the first seat). */
export async function getWorkspaces(
  { skipBootstrap = false, skip = 0, limit = 50 }: { skipBootstrap?: boolean; skip?: number; limit?: number } = {}
): Promise<WorkspacesResponse> {
  return callAuthRoute("GET", `/workspaces?skip=${skip}&limit=${limit}${skipBootstrap ? "&skipBootstrap=true" : ""}`);
}

export async function createWorkspace(data: {
  name: string;
  handle?: string;
  instructions?: string;
  ownerUserId?: string;
}): Promise<Workspace> {
  return callFunction("adminCreateWorkspace", data);
}

export async function createOwnWorkspace(data: {
  name: string;
}): Promise<Workspace> {
  return callFunction("createOwnWorkspace", data);
}

export async function updateOwnWorkspace(
  workspaceId: string,
  data: { name: string }
): Promise<Workspace> {
  return callFunction("updateOwnWorkspace", { workspaceId, ...data });
}

export async function updateWorkspace(
  workspaceId: string,
  data: { name?: string; handle?: string; instructions?: string }
): Promise<Workspace> {
  return callFunction("adminUpdateWorkspace", { workspaceId, ...data });
}

export async function deleteWorkspace(workspaceId: string): Promise<void> {
  return callFunction("adminDeleteWorkspace", { workspaceId });
}

// =====================================================
// Workspace Collaborators
// =====================================================

// T-330 batch 5a: the workspace is in the path.
const collaboratorsPath = (workspaceId: string) => `/workspaces/${seg(workspaceId)}/collaborators`;

export async function getWorkspaceCollaborators(
  workspaceId: string,
  role?: string
): Promise<WorkspaceCollaboratorsResponse> {
  return callRoute("GET", `${collaboratorsPath(workspaceId)}${role ? `?role=${seg(role)}` : ""}`);
}

export async function removeWorkspaceCollaborator(
  workspaceId: string,
  userId: string
): Promise<void> {
  return callRoute("DELETE", `${collaboratorsPath(workspaceId)}/${seg(userId)}`);
}

// =====================================================
// Business profiles (T-243): letterhead data of a workspace, owner/admin only
// =====================================================

export interface BusinessCredential {
  label: string;
  number: string;
}

export interface BusinessProfileInput {
  name: string;
  legal_name: string;
  trade_name: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  region: string | null;
  postal_code: string | null;
  country: string;
  phone: string | null;
  email: string | null;
  website: string | null;
  main_contact_user_id: string | null;
  main_contact_title: string | null;
  neq: string | null;
  gst_number: string | null;
  qst_number: string | null;
  credentials: BusinessCredential[];
}

export interface BusinessProfile extends BusinessProfileInput {
  id: string;
  workspace_id: string;
  is_default: boolean;
  logo_storage_key: string | null;
  /** Signed GET, valid 15 minutes. */
  logo_url: string | null;
  created_at: string;
  updated_at: string;
}

export type BusinessProfileChanges = Partial<BusinessProfileInput> & {
  is_default?: true;
  logo_storage_key?: string | null;
};

export type BusinessLogoContentType = "image/png" | "image/jpeg";

export async function listBusinessProfiles(): Promise<{ profiles: BusinessProfile[] }> {
  return callFunction("listBusinessProfiles", {});
}

export async function createBusinessProfile(profile: BusinessProfileInput): Promise<{ profile: BusinessProfile }> {
  return callFunction("createBusinessProfile", { profile });
}

export async function updateBusinessProfile(
  profileId: string,
  changes: BusinessProfileChanges
): Promise<{ profile: BusinessProfile }> {
  return callFunction("updateBusinessProfile", { profileId, changes });
}

export async function deleteBusinessProfile(profileId: string): Promise<{ ok: true }> {
  return callFunction("deleteBusinessProfile", { profileId });
}

export async function getBusinessProfileLogoUploadUrl(
  profileId: string,
  contentType: BusinessLogoContentType
): Promise<{ uploadUrl: string; storageKey: string; expiresAt: string }> {
  return callFunction("getBusinessProfileLogoUploadUrl", { profileId, contentType });
}

// =====================================================
// Invitations and first workspace (T-183 P3)
// =====================================================

// Called straight through callApp / callAuth, not callRoute: the caller needs the raw error code
// (redeemInvite tells a used link from an unknown one, PendingInvitations hides on permission-denied).

export type InviteRole = "admin" | "editor" | "viewer" | "uploader";

export interface CreateInviteRequest {
  workspaceId: string;
  role: InviteRole;
  projectIds?: string[];
  inviteeName?: string;
  inviteeEmail?: string;
  inviteePhone?: string;
  send?: boolean;
}

export interface CreateInviteResponse {
  inviteId: string;
  link: string;
  expiresAt: string;
  sentVia: "email" | "sms" | null;
  sendError: string | null;
}

export interface PendingInvite {
  inviteId: string;
  role: InviteRole;
  projectIds: string[];
  inviteeName: string | null;
  inviteeEmail: string | null;
  inviteePhone: string | null;
  expiresAt: string;
  createdAt: string;
}

export interface RedeemInviteResponse {
  workspaceId: string;
  workspaceName: string;
  role: InviteRole;
  projectIds: string[];
}

export interface InvitePreview {
  workspaceName: string;
  role: InviteRole;
  currentRole: InviteRole | null;
  resultingRole: InviteRole;
  projectNames: string[];
}

const invitesPath = (workspaceId: string) => `/workspaces/${seg(workspaceId)}/invites`;

export const createInvite = ({ workspaceId, ...invite }: CreateInviteRequest) =>
  callApp<CreateInviteResponse>("POST", invitesPath(workspaceId), invite);

export const listInvites = (workspaceId: string) => callApp<{ invites: PendingInvite[] }>("GET", invitesPath(workspaceId));

export const revokeInvite = (workspaceId: string, inviteId: string) =>
  callApp<void>("DELETE", `${invitesPath(workspaceId)}/${seg(inviteId)}`);

// authProxy (hub-auth): a redeem may first resolve a caller with no claim, which only an identity writer may do.
export const previewInvite = (token: string) => callAuth<InvitePreview>("POST", "/invite-preview", { token }, false);

export const redeemInvite = (token: string) => callAuth<RedeemInviteResponse>("POST", "/invite-redeem", { token });

export async function updateWorkspaceCollaborator(
  workspaceId: string,
  userId: string,
  data: { role?: WorkspaceCollaboratorRole; permissions?: string[] | null }
): Promise<WorkspaceCollaborator> {
  return callRoute("PATCH", `${collaboratorsPath(workspaceId)}/${seg(userId)}`, data);
}

export async function getWorkspaceMemberLabels(workspaceId: string): Promise<{ labels: MemberLabel[] }> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/member-labels`);
}

export async function setWorkspaceMemberLabel(
  workspaceId: string,
  userId: string,
  label: MemberLabelFields
): Promise<MemberLabel> {
  return callRoute("PUT", `/workspaces/${seg(workspaceId)}/member-labels/${seg(userId)}`, label);
}

// =====================================================
// AI agent members (T-358): nara-core enforces who may manage them
// =====================================================

export type AgentRole = "viewer" | "editor";

export interface AgentMember {
  user_id: string;
  display_name: string;
  role: AgentRole;
}

export interface AgentKeyInfo {
  key_id: string;
  key_prefix: string;
  active: boolean;
  created_at: string;
  last_used_at: string | null;
}

/** `key` is the raw key: nara-core returns it once and never again. */
export interface AgentKeyCreated {
  key_id: string;
  key: string;
  key_prefix: string;
}

const agentMembersPath = (workspaceId: string) => `/workspaces/${seg(workspaceId)}/agent-members`;

export const createAgentMember = (workspaceId: string, body: { display_name: string; role: AgentRole }) =>
  callRoute<AgentMember>("POST", agentMembersPath(workspaceId), body);

export const listAgentKeys = (workspaceId: string, userId: string) =>
  callRoute<{ keys: AgentKeyInfo[] }>("GET", `${agentMembersPath(workspaceId)}/${seg(userId)}/keys`);

export const createAgentKey = (workspaceId: string, userId: string) =>
  callRoute<AgentKeyCreated>("POST", `${agentMembersPath(workspaceId)}/${seg(userId)}/keys`);

export const revokeAgentKey = (workspaceId: string, userId: string, keyId: string) =>
  callRoute<void>("POST", `${agentMembersPath(workspaceId)}/${seg(userId)}/keys/${seg(keyId)}/revoke`);

// =====================================================
// Projects
// =====================================================

export async function getProjects(
  workspaceId: string,
  skip = 0,
  limit = 100
): Promise<ProjectsResponse> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/projects?${new URLSearchParams({ skip: String(skip), limit: String(limit) })}`);
}

export async function getProject(projectId: string, workspaceIdOverride?: string): Promise<Project> {
  // If override provided, use it; otherwise callRoute sends the current workspace
  return callRoute("GET", `/projects/${seg(projectId)}`, undefined, workspaceIdOverride);
}

export async function createProject(data: {
  workspaceId: string;
  name: string;
  description?: string;
  instructions?: string;
  tagIds?: string[];
}): Promise<Project> {
  const { workspaceId, ...body } = data;
  return callRoute("POST", `/workspaces/${seg(workspaceId)}/projects`, body);
}

export async function updateProject(
  projectId: string,
  data: { name?: string; description?: string; instructions?: string; templateId?: string | null; tagIds?: string[] }
): Promise<Project> {
  return callRoute("PUT", `/projects/${seg(projectId)}`, data);
}

export async function deleteProject(projectId: string): Promise<void> {
  return callRoute("DELETE", `/projects/${seg(projectId)}`);
}

export async function getDeletedProjects(
  workspaceId: string
): Promise<ProjectsResponse> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/projects/deleted`);
}

export async function restoreProject(projectId: string): Promise<Project> {
  return callRoute("POST", `/projects/${seg(projectId)}/restore`);
}

// =====================================================
// Tags
// =====================================================

export async function getTags(workspaceId: string): Promise<{ tags: Tag[]; total: number }> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/tags`);
}

export async function createTag(data: {
  workspaceId: string;
  name: string;
  description?: string;
  color?: string;
}): Promise<Tag> {
  const { workspaceId, ...body } = data;
  return callRoute("POST", `/workspaces/${seg(workspaceId)}/tags`, body);
}

export async function updateTag(
  tagId: string,
  data: { name?: string; description?: string; color?: string },
  workspaceId?: string,
): Promise<Tag> {
  return callRoute("PUT", `/tags/${seg(tagId)}`, data, workspaceId);
}

export async function deleteTag(tagId: string, workspaceId?: string): Promise<void> {
  return callRoute("DELETE", `/tags/${seg(tagId)}`, undefined, workspaceId);
}

export async function setProjectTags(
  projectId: string,
  tagIds: string[],
  workspaceId?: string,
): Promise<TagSummary[]> {
  return callRoute("PUT", `/projects/${seg(projectId)}/tags`, { tagIds }, workspaceId);
}

// =====================================================
// Project Collaborators
// =====================================================

// T-330 batch 5a: the workspace is in the path.
const projectCollaboratorsPath = (workspaceId: string, projectId: string) =>
  `/workspaces/${seg(workspaceId)}/projects/${seg(projectId)}/collaborators`;

export async function getProjectCollaborators(
  workspaceId: string,
  projectId: string
): Promise<CollaboratorsResponse> {
  return callRoute("GET", projectCollaboratorsPath(workspaceId, projectId));
}

export async function addProjectCollaborator(
  workspaceId: string,
  projectId: string,
  data: { userId: string; emailTranscription?: boolean }
): Promise<Collaborator> {
  return callRoute("POST", projectCollaboratorsPath(workspaceId, projectId), data);
}

export async function updateProjectCollaborator(
  workspaceId: string,
  projectId: string,
  userId: string,
  data: { emailTranscription?: boolean }
): Promise<Collaborator> {
  return callRoute("PATCH", `${projectCollaboratorsPath(workspaceId, projectId)}/${seg(userId)}`, data);
}

export async function removeProjectCollaborator(
  workspaceId: string,
  projectId: string,
  userId: string
): Promise<void> {
  return callRoute("DELETE", `${projectCollaboratorsPath(workspaceId, projectId)}/${seg(userId)}`);
}

// =====================================================
// Documents
// =====================================================

export async function getProjectDocuments(
  workspaceId: string,
  projectId: string,
  skip = 0,
  limit = 100,
  deleted = false
): Promise<DocumentsResponse> {
  const query = new URLSearchParams({ skip: String(skip), limit: String(limit), ...(deleted && { deleted: "true" }) });
  return callRoute("GET", `/projects/${seg(projectId)}/documents?${query}`, undefined, workspaceId);
}

// =====================================================
// Product catalog (T-365): nara-documents decides who may read or change a record
// =====================================================

export type ProductRecordStatus = "needs_review" | "approved" | "outdated";
export type ProductData = Record<string, unknown>;

export interface ProductRecordItem {
  id: string;
  document_id: string;
  position: number;
  status: ProductRecordStatus;
  data: ProductData;
  approved_at: string | null;
  updated_at: string;
}

export interface ProductRecordsPage {
  items: ProductRecordItem[];
  next_cursor: string | null;
}

export interface ProductRecordDetail extends ProductRecordItem {
  /** What the AI first read: never changed by an edit. */
  extracted_data: ProductData;
  extraction: { id: string; recipe_id: string; recipe_version: number; record_schema: RecordSchema };
  document: { id: string; name: string | null };
}

export interface ProductExtraction {
  id: string;
  status: "extracting" | "done" | "failed";
  failure_reason: string | null;
  record_count: number | null;
  recipe_version: number;
  created_at: string;
  finished_at: string | null;
}

export interface ProductRecordFilters {
  status?: ProductRecordStatus;
  q?: string;
  limit?: number;
  cursor?: string;
}

export async function listProductRecords(workspaceId: string, projectId: string, filters: ProductRecordFilters): Promise<ProductRecordsPage> {
  const { status, q, limit, cursor } = filters;
  const query = new URLSearchParams({
    ...(status && { status }),
    ...(q && { q }),
    ...(limit && { limit: String(limit) }),
    ...(cursor && { cursor }),
  }).toString();
  return callRoute("GET", `/projects/${seg(projectId)}/product-records${query && `?${query}`}`, undefined, workspaceId);
}

export async function getProductRecord(recordId: string): Promise<ProductRecordDetail> {
  return callRoute("GET", `/product-records/${seg(recordId)}`);
}

export async function updateProductRecord(
  recordId: string,
  patch: { data?: ProductData; status?: "approved" | "needs_review" }
): Promise<ProductRecordDetail> {
  return callRoute("PATCH", `/product-records/${seg(recordId)}`, patch);
}

export async function getDocumentProductExtractions(documentId: string): Promise<ProductExtraction[]> {
  return callRoute("GET", `/documents/${seg(documentId)}/product-extractions`);
}

// A document's /app route: under its project, like the phones (T-330 batch 4a).
const docPath = (projectId: string, documentId: string) => `/projects/${seg(projectId)}/documents/${seg(documentId)}`;

export async function getDocument(projectId: string, documentId: string): Promise<Document> {
  return callRoute("GET", docPath(projectId, documentId));
}

export async function getDocumentView(projectId: string, documentId: string, workspaceId?: string): Promise<DocumentView> {
  return callRoute("GET", `${docPath(projectId, documentId)}/transcript`, undefined, workspaceId);
}

export async function createDocument(
  projectId: string,
  data: {
    name: string;
    status?: string;
    files?: Array<{
      name: string;
      url?: string;
      mimeType?: string;
      size?: number;
    }>;
    metadata?: Record<string, unknown>;
    contentMd?: string;
    documentType?: string;
  }
): Promise<Document> {
  return callRoute("POST", `/projects/${seg(projectId)}/documents`, data);
}

export async function updateDocumentName(
  projectId: string,
  documentId: string,
  name: string
): Promise<Document> {
  return callRoute("PATCH", docPath(projectId, documentId), { name });
}

export async function updateDocumentContent(
  projectId: string,
  documentId: string,
  contentJson: unknown
): Promise<Document> {
  return callRoute("PATCH", `${docPath(projectId, documentId)}/transcript`, { content_json: contentJson });
}

export async function updateDocumentMarkdown(
  projectId: string,
  documentId: string,
  contentMd: string
): Promise<Document> {
  return callRoute("PATCH", `${docPath(projectId, documentId)}/markdown`, { contentMd });
}

export async function deleteDocument(
  projectId: string,
  documentId: string
): Promise<void> {
  return callRoute("DELETE", docPath(projectId, documentId));
}

export async function restoreDocument(
  projectId: string,
  documentId: string
): Promise<Document> {
  return callRoute("POST", `${docPath(projectId, documentId)}/restore`);
}

export async function hardDeleteDocument(
  projectId: string,
  documentId: string
): Promise<void> {
  return callRoute("DELETE", `${docPath(projectId, documentId)}/permanent`);
}

export async function hardDeleteProject(
  projectId: string
): Promise<void> {
  return callRoute("DELETE", `/projects/${seg(projectId)}/permanent`);
}

export async function processDocument(projectId: string, documentId: string): Promise<{
  job_id: string;
  status: string;
}> {
  return callRoute("POST", `${docPath(projectId, documentId)}/process`);
}

export async function markDocumentReady(projectId: string, documentId: string): Promise<Document> {
  return callRoute("POST", `${docPath(projectId, documentId)}/ready`);
}

export async function deleteTranscription(projectId: string, documentId: string): Promise<{
  status: string;
  document_id: string;
  files_deleted: number;
  gcs_folders_cleaned: string[];
}> {
  return callRoute("DELETE", `${docPath(projectId, documentId)}/transcription`);
}

export async function generateDocumentPdf(
  projectId: string,
  documentId: string,
  recipeId?: string
): Promise<{
  success: boolean;
  document_id: string;
  generated_document_id?: string;
  file_path: string;
  file_size_bytes: number;
}> {
  return callRoute("POST", `${docPath(projectId, documentId)}/pdf`, { recipeId });
}

export async function getDocumentGeneratedDocuments(projectId: string, documentId: string): Promise<ProjectGeneratedDocumentsResponse> {
  return callRoute("GET", `${docPath(projectId, documentId)}/generated-documents`);
}

export async function deleteDocumentGeneratedDocument(projectId: string, documentId: string, generatedDocumentId: string): Promise<void> {
  return callRoute("DELETE", `${docPath(projectId, documentId)}/generated-documents/${seg(generatedDocumentId)}`);
}

// =====================================================
// Document Versions
// =====================================================

export async function getDocumentVersions(
  projectId: string,
  documentId: string
): Promise<DocumentVersionSummary[]> {
  return callRoute("GET", `${docPath(projectId, documentId)}/versions`);
}

export async function getDocumentVersion(
  projectId: string,
  documentId: string,
  versionId: string
): Promise<DocumentVersionDetail> {
  return callRoute("GET", `${docPath(projectId, documentId)}/versions/${seg(versionId)}`);
}

export async function createDocumentVersion(
  projectId: string,
  documentId: string,
  label?: string
): Promise<DocumentVersionSummary> {
  return callRoute("POST", `${docPath(projectId, documentId)}/versions`, { label });
}

export async function restoreDocumentVersion(
  projectId: string,
  documentId: string,
  versionId: string
): Promise<DocumentVersionDetail> {
  return callRoute("POST", `${docPath(projectId, documentId)}/versions/${seg(versionId)}/restore`);
}

// =====================================================
// Project Templates (Non-Admin)
// =====================================================

export async function getWorkspaceProjectTemplates(
  workspaceId: string
): Promise<ProjectTemplatesResponse> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/templates`);
}

// =====================================================
// Project Templates (Admin Only)
// =====================================================

export async function getProjectTemplates(
  skip = 0,
  limit = 100
): Promise<ProjectTemplatesResponse> {
  return callFunction("adminGetProjectTemplates", { skip, limit });
}

export async function getProjectTemplate(
  templateId: string
): Promise<ProjectTemplate> {
  return callFunction("adminGetProjectTemplate", { templateId });
}

export async function createProjectTemplate(data: {
  name: string;
  description?: string;
  scope?: "global" | "workspace";
  isWorkspaceShared?: boolean;
}): Promise<ProjectTemplate> {
  return callFunction("adminCreateProjectTemplate", data);
}

export async function updateProjectTemplate(
  templateId: string,
  data: {
    name?: string;
    description?: string;
    scope?: "global" | "workspace";
    workspaceId?: string;
    documentRecipeIds?: string[];
    defaultTranscriptionRecipeId?: string;
    defaultProjectRecipeId?: string;
    defaultExtractionRecipeId?: string;
    isWorkspaceShared?: boolean;
  }
): Promise<ProjectTemplate> {
  // Explicitly include workspaceId key to prevent callFunction from auto-injecting
  // the active workspace. Only an explicit value should set template ownership.
  return callFunction("adminUpdateProjectTemplate", {
    templateId,
    ...data,
    workspaceId: data.workspaceId,
  });
}

export async function deleteProjectTemplate(
  templateId: string
): Promise<void> {
  return callFunction("adminDeleteProjectTemplate", { templateId });
}

export async function setDefaultProjectTemplate(
  templateId: string
): Promise<ProjectTemplate> {
  return callFunction("adminSetDefaultProjectTemplate", { templateId });
}

export async function duplicateProjectTemplate(
  templateId: string
): Promise<ProjectTemplate> {
  return callFunction("adminDuplicateProjectTemplate", { templateId });
}

export interface ProjectTemplateUsage {
  workspaceCount: number;
  projectCount: number;
  canDelete: boolean;
  isDefault: boolean;
}

export async function getProjectTemplateUsage(
  templateId: string
): Promise<ProjectTemplateUsage> {
  return callFunction("adminGetProjectTemplateUsage", { templateId });
}

export async function addWorkspaceToTemplate(
  templateId: string,
  workspaceId: string
): Promise<void> {
  return callFunction("adminAddWorkspaceToTemplate", { templateId, workspaceId });
}

export async function removeWorkspaceFromTemplate(
  templateId: string,
  workspaceId: string
): Promise<void> {
  return callFunction("adminRemoveWorkspaceFromTemplate", { templateId, workspaceId });
}

// =====================================================
// NaraShape Recipes (Admin Only)
// =====================================================

export async function getShapeRecipes(): Promise<ShapeRecipesResponse> {
  return callFunction("adminGetShapeRecipes", {});
}

export async function getShapeRecipe(recipeId: string): Promise<ShapeRecipe> {
  return callFunction("adminGetShapeRecipe", { templateId: recipeId });
}

export async function createShapeRecipe(data: {
  name: string;
  input_mode?: string;
  description?: string;
  code?: string;
  prompt?: string;
  locale?: string;
  schema?: Record<string, unknown>;
}): Promise<ShapeRecipe> {
  return callFunction("adminCreateShapeRecipe", data);
}

export async function updateShapeRecipe(
  recipeId: string,
  data: {
    name?: string;
    input_mode?: string;
    description?: string;
    code?: string;
    prompt?: string;
    template_html?: string;
    locale?: string;
    schema?: Record<string, unknown>;
    /** An extraction recipe holds none of these: a switch clears them ("", {} and []). */
    gemini_sources?: Record<string, unknown>;
    document_references?: Array<Record<string, unknown>>;
  }
): Promise<ShapeRecipe> {
  return callFunction("adminUpdateShapeRecipe", { templateId: recipeId, ...data });
}

export async function deleteShapeRecipe(recipeId: string): Promise<void> {
  return callFunction("adminDeleteShapeRecipe", { templateId: recipeId });
}

export interface ShapeValidationResult {
  valid: boolean;
  message: string;
  diagnostic?: string;
  is_data_error: boolean;
}

export async function validateShapeRecipeCode(
  code: string,
  schema?: Record<string, unknown>
): Promise<ShapeValidationResult> {
  return callFunction("adminValidateShapeRecipeCode", { code, schema });
}

// =====================================================
// NaraShape Recipe Versioning (Admin Only)
// =====================================================

export async function getShapeRecipeDraft(recipeId: string): Promise<ShapeRecipe> {
  return callFunction("adminGetShapeRecipeDraft", { templateId: recipeId });
}

export async function getShapeRecipeVersions(recipeId: string): Promise<ShapeRecipeVersionsResponse> {
  return callFunction("adminGetShapeRecipeVersions", { templateId: recipeId });
}

export async function getShapeRecipeVersion(recipeId: string, version: number): Promise<ShapeRecipeVersion> {
  return callFunction("adminGetShapeRecipeVersion", { templateId: recipeId, version });
}

export async function publishShapeRecipe(recipeId: string): Promise<{ version: number; published_at: string }> {
  return callFunction("adminPublishShapeRecipe", { templateId: recipeId });
}

export async function activateShapeRecipeVersion(recipeId: string, version: number): Promise<{ active_version: number; has_draft_changes: boolean; status: string }> {
  return callFunction("adminActivateShapeRecipeVersion", { templateId: recipeId, version });
}

// =====================================================
// Document Structure Extraction (Admin Only)
// =====================================================

export async function adminExtractDocumentStructure(
  documentId: string,
  workspaceId: string,
  fileId?: string
): Promise<DocumentStructureResponse> {
  return callFunction("adminExtractDocumentStructure", { documentId, workspaceId, fileId });
}

export async function generateRecipePreviewPdf(data: {
  recipeId: string;
  documentId: string;
  workspaceId?: string;
  useDraft?: boolean;
}): Promise<{ success: boolean; document_id?: string; content_base64?: string; message?: string }> {
  return callFunction("generateRecipePreviewPdf", data);
}

// =====================================================
// File Import
// =====================================================

export interface UploadFile {
  filename: string;
  content_type: string;
  size_bytes: number;
}

export interface UploadUrls {
  document_id: string;
  uploads: Array<{
    file_id: string;
    filename: string;
    signed_url: string;
    expires_at: string;
  }>;
}

export async function importFiles(projectId: string, data: { documentTitle: string; files: UploadFile[] }): Promise<UploadUrls> {
  return callRoute("POST", `/projects/${seg(projectId)}/imports`, data);
}

export async function rotatePhoto(data: {
  fileId: string;
  workspaceId: string;
  projectId: string;
  documentId: string;
  angle: 90 | 180 | 270;
}): Promise<{ success: boolean }> {
  const path = `${workspaceProjectPath(data.workspaceId, data.projectId)}/documents/${seg(data.documentId)}/files/${seg(data.fileId)}/rotate`;
  return callRoute("POST", path, { angle: data.angle });
}

export async function deleteFile(projectId: string, documentId: string, fileId: string): Promise<void> {
  return callRoute("DELETE", `${docPath(projectId, documentId)}/files/${seg(fileId)}`);
}

export async function addPhotosToDocument(projectId: string, documentId: string, files: UploadFile[]): Promise<UploadUrls> {
  return callRoute("POST", `${docPath(projectId, documentId)}/photos`, { files });
}

// =====================================================
// Project Generate & Chat
// =====================================================

export interface ProjectContentResponse {
  project_id: string;
  project_name: string;
  documents: Array<{
    id: string;
    name: string;
    word_count: number;
    has_content_json: boolean;
    image_count: number;
    document_type?: string;
    status: string;
  }>;
  images: Array<{
    id: string;
    document_id: string;
    filename: string;
    mime_type?: string;
  }>;
  stats: {
    total_documents: number;
    documents_with_text: number;
    total_word_count: number;
    total_images: number;
  };
  shared_projects?: Array<{
    id: string;
    name: string;
    document_count: number;
  }>;
}

export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

export interface ProjectChatResponse {
  response: string;
  session_id: string;
  sources: Array<{
    document_id: string;
    document_name: string;
    relevance_score?: number;
    project_id?: string;
    project_name?: string;
  }>;
}

export interface ChatSessionSummary {
  id: string;
  title?: string;
  scope: string;
  project_id?: string;
  message_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChatSessionsListResponse {
  sessions: ChatSessionSummary[];
  count: number;
}

export interface ChatMessageItem {
  id: string;
  role: string;
  content: string;
  sources?: ProjectChatResponse["sources"];
  created_at?: string;
}

export interface ChatSessionDetail {
  id: string;
  title?: string;
  scope: string;
  project_id?: string;
  messages: ChatMessageItem[];
  created_at?: string;
  updated_at?: string;
}

export interface ProjectGenerateResponse {
  status: string;
  generated_document_id: string;
  file_path?: string;
  file_size_bytes?: number;
}

export interface GeneratedDocumentItem {
  id: string;
  name: string;
  description?: string;
  document_type?: string;
  template_used?: string;
  file_path?: string;
  file_size_bytes?: number;
  download_url?: string;
  attachment_url?: string;
  output_format?: "pdf" | "html_page" | "html_bundle";
  created_at?: string;
  status?: string;
  status_detail?: string;
  error_message?: string;
}

export interface ProjectGeneratedDocumentsResponse {
  documents: GeneratedDocumentItem[];
  count: number;
}

// A project of a workspace: the generate and chat routes (T-330 batch 6) name both.
const workspaceProjectPath = (workspaceId: string, projectId: string) => `/workspaces/${seg(workspaceId)}/projects/${seg(projectId)}`;
const chatSessionsPath = (workspaceId: string) => `/workspaces/${seg(workspaceId)}/chat-sessions`;

export async function getProjectGeneratedDocuments(workspaceId: string, projectId: string): Promise<ProjectGeneratedDocumentsResponse> {
  return callRoute("GET", `${workspaceProjectPath(workspaceId, projectId)}/generated-documents`);
}

export async function deleteProjectGeneratedDocument(workspaceId: string, projectId: string, generatedDocumentId: string): Promise<void> {
  return callRoute("DELETE", `${workspaceProjectPath(workspaceId, projectId)}/generated-documents/${seg(generatedDocumentId)}`);
}

export async function getProjectContent(workspaceId: string, projectId: string): Promise<ProjectContentResponse> {
  return callRoute("GET", `${workspaceProjectPath(workspaceId, projectId)}/content`);
}

// The path names the scope: a workspace chat searches every project, a project chat its project.
export async function projectChat(
  workspaceId: string,
  { projectId, scope, ...body }: {
    projectId?: string;
    message: string;
    history?: ChatMessage[];
    scope?: string;
    sessionId?: string;
    contextSessionIds?: string[];
  }
): Promise<ProjectChatResponse> {
  const path = scope === "workspace" ? `/workspaces/${seg(workspaceId)}/chat` : `${workspaceProjectPath(workspaceId, projectId ?? "")}/chat`;
  return callRoute("POST", path, body);
}

export async function listChatSessions(workspaceId: string, params: {
  projectId?: string;
  scope?: string;
}): Promise<ChatSessionsListResponse> {
  const query = String(new URLSearchParams(Object.entries(params).filter((entry): entry is [string, string] => !!entry[1])));
  return callRoute("GET", `${chatSessionsPath(workspaceId)}${query ? `?${query}` : ""}`);
}

export async function getChatSession(workspaceId: string, sessionId: string): Promise<ChatSessionDetail> {
  return callRoute("GET", `${chatSessionsPath(workspaceId)}/${seg(sessionId)}`);
}

export async function updateChatSession(workspaceId: string, sessionId: string, title: string): Promise<{ id: string; title: string }> {
  return callRoute("PATCH", `${chatSessionsPath(workspaceId)}/${seg(sessionId)}`, { title });
}

export async function deleteChatSession(workspaceId: string, sessionId: string): Promise<{ status: string }> {
  return callRoute("DELETE", `${chatSessionsPath(workspaceId)}/${seg(sessionId)}`);
}

export async function generateProjectDocument(
  workspaceId: string,
  { projectId, ...body }: { projectId: string; templateId: string; locale?: string }
): Promise<ProjectGenerateResponse> {
  return callRoute("POST", `${workspaceProjectPath(workspaceId, projectId)}/generate`, body);
}

export async function getProjectShapeRecipes(workspaceId: string, projectId: string): Promise<ShapeRecipesResponse> {
  return callRoute("GET", `${workspaceProjectPath(workspaceId, projectId)}/shape-recipes`);
}

/** The project recipes of one of the workspace's project templates (T-366 auto-project pick). */
export async function getTemplateShapeRecipes(workspaceId: string, templateId: string): Promise<ShapeRecipesResponse> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/templates/${seg(templateId)}/shape-recipes`);
}

// =====================================================
// Workspace Settings (NaraInterface)
// =====================================================

export async function getWorkspaceSettings(
  workspaceId: string,
  interfaceId: string
): Promise<WorkspaceSettings> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/settings?${new URLSearchParams({ interfaceId })}`);
}

export async function updateWorkspaceSettings(
  workspaceId: string,
  interfaceId: string,
  preferences: Record<string, unknown>
): Promise<WorkspaceSettings> {
  return callRoute("PATCH", `/workspaces/${seg(workspaceId)}/settings`, { interfaceId, preferences });
}

// =====================================================
// Admin - Workspace Management
// =====================================================

export interface AdminWorkspace extends Workspace {
  is_blocked?: boolean;
  blocked_at?: string;
  blocked_reason?: string;
  deleted_at?: string;
  member_count?: number;
  project_count?: number;
  is_dev?: boolean;
  billing_hidden?: boolean;
}

export interface AdminWorkspacesResponse {
  workspaces: AdminWorkspace[];
  total?: number;
}

export interface AdminWorkspaceDetail extends AdminWorkspace {
  collaborators?: WorkspaceCollaborator[];
  templates?: ProjectTemplate[];
}

export async function adminGetAllWorkspaces(
  skip = 0,
  limit = 50,
  search?: string,
  includeBlocked = true
): Promise<AdminWorkspacesResponse> {
  return callFunction("adminGetAllWorkspaces", { skip, limit, search, includeBlocked });
}

export async function adminGetWorkspaceDetail(
  workspaceId: string
): Promise<AdminWorkspaceDetail> {
  return callFunction("adminGetWorkspaceDetail", { workspaceId });
}

export async function adminUpdateWorkspace(
  workspaceId: string,
  data: { name?: string; handle?: string; settings?: Record<string, any>; is_dev?: boolean }
): Promise<AdminWorkspace> {
  // Filter out undefined values to prevent null being sent to the API
  const payload: Record<string, any> = { workspaceId };
  if (data.name !== undefined) payload.name = data.name;
  if (data.handle !== undefined) payload.handle = data.handle;
  if (data.settings !== undefined) payload.settings = data.settings;
  if (data.is_dev !== undefined) payload.is_dev = data.is_dev;
  return callFunction("adminUpdateWorkspace", payload);
}

export async function adminBlockWorkspace(
  workspaceId: string,
  reason?: string
): Promise<AdminWorkspace> {
  return callFunction("adminBlockWorkspace", { workspaceId, reason });
}

export async function adminUnblockWorkspace(
  workspaceId: string
): Promise<AdminWorkspace> {
  return callFunction("adminUnblockWorkspace", { workspaceId });
}

export async function adminSoftDeleteWorkspace(
  workspaceId: string
): Promise<AdminWorkspace> {
  return callFunction("adminSoftDeleteWorkspace", { workspaceId });
}

export async function adminRestoreWorkspace(
  workspaceId: string
): Promise<AdminWorkspace> {
  return callFunction("adminRestoreWorkspace", { workspaceId });
}

export async function adminAddWorkspaceCollaboratorDirect(
  workspaceId: string,
  data: { userId: string; role: string }
): Promise<WorkspaceCollaborator> {
  return callFunction("adminAddWorkspaceCollaboratorDirect", { workspaceId, ...data });
}

export async function adminRemoveWorkspaceCollaborator(
  workspaceId: string,
  userId: string
): Promise<void> {
  return callFunction("adminRemoveWorkspaceCollaborator", { workspaceId, userId });
}

export interface AdminCollaboratorDetailResponse {
  user_id: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  workspace_role?: WorkspaceCollaboratorRole | null;
  workspace_collaborator_status?: "active" | "invited" | "deactivated";
  is_on_any_project?: boolean;
  projects: CollaboratorProjectAssociation[];
}

export async function adminGetCollaboratorDetail(
  workspaceId: string,
  userId: string
): Promise<AdminCollaboratorDetailResponse> {
  return callFunction("adminGetCollaboratorDetail", { workspaceId, userId });
}

export async function adminUpdateCollaborator(
  userId: string,
  data: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  }
): Promise<void> {
  // Filter out undefined values
  const payload: Record<string, string> = { userId };
  if (data.email !== undefined) payload.email = data.email;
  if (data.phone !== undefined) payload.phone = data.phone;
  if (data.firstName !== undefined) payload.firstName = data.firstName;
  if (data.lastName !== undefined) payload.lastName = data.lastName;
  return callFunction("adminUpdateCollaborator", payload);
}

export async function adminGetWorkspaceProjects(
  workspaceId: string,
  skip = 0,
  limit = 100
): Promise<ProjectsResponse> {
  return callFunction("adminGetWorkspaceProjects", { workspaceId, skip, limit });
}

export async function adminGetWorkspaceCollaborators(
  workspaceId: string,
  role?: string
): Promise<WorkspaceCollaboratorsResponse> {
  return callFunction("adminGetWorkspaceCollaborators", { workspaceId, role });
}

export async function adminUpdateWorkspaceCollaboratorRole(
  workspaceId: string,
  userId: string,
  data: { role?: WorkspaceCollaboratorRole; permissions?: string[] | null }
): Promise<WorkspaceCollaborator> {
  return callFunction("adminUpdateWorkspaceCollaboratorRole", { workspaceId, userId, ...data });
}

export async function adminGetWorkspaceTemplates(
  workspaceId: string
): Promise<ProjectTemplatesResponse> {
  return callFunction("adminGetWorkspaceTemplates", { workspaceId });
}

export async function adminAssignProjectTemplates(
  workspaceId: string,
  templateIds: string[]
): Promise<void> {
  return callFunction("adminAssignProjectTemplates", { workspaceId, templateIds });
}

export async function getWorkspaceCollaboratorDetail(
  workspaceId: string,
  userId: string
): Promise<CollaboratorDetailResponse> {
  return callRoute("GET", `${collaboratorsPath(workspaceId)}/${seg(userId)}`);
}

// =====================================================
// Admin - Project Collaborator Management
// =====================================================

export async function adminGetProjectCollaborators(
  projectId: string
): Promise<CollaboratorsResponse> {
  return callFunction("adminGetProjectCollaborators", { projectId });
}

export async function adminAddProjectCollaboratorDirect(
  projectId: string,
  data: { userId: string; emailTranscription?: boolean }
): Promise<Collaborator> {
  return callFunction("adminAddProjectCollaboratorDirect", { projectId, ...data });
}

export async function adminUpdateProjectCollaborator(
  projectId: string,
  userId: string,
  data: { emailTranscription?: boolean }
): Promise<Collaborator> {
  return callFunction("adminUpdateProjectCollaborator", { projectId, userId, ...data });
}

export async function adminRemoveProjectCollaborator(
  projectId: string,
  userId: string
): Promise<void> {
  return callFunction("adminRemoveProjectCollaborator", { projectId, userId });
}

// =====================================================
// Admin - Boost Usage
// =====================================================

export interface BoostOperationBreakdown {
  service: string;
  processor: string;
  totalBoostsCharged: number;
  providerCostUsd: number;
  transactionCount: number;
}

export interface BoostWorkflowBreakdown {
  naraFlowId: string;
  documentId: string;
  totalBoostsCharged: number;
  providerCostUsd: number;
  transactionCount: number;
  createdAt: string | null;
  stt_model?: string | null;
  workflowType?: string | null;
  transcriptionWorkflow?: string | null;
  workflowLabel?: string | null;
  audioDurationSec?: number | null;
  imageCount?: number;
  boostsPerMinute?: number | null;
  providerCostPerMinute?: number | null;
}

export interface BoostWorkflowUsed {
  workflowType: string | null;
  transcriptionWorkflow: string | null;
  label: string;
  flowCount: number;
}


export interface BoostTransaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter?: number;
  description?: string;
  model?: string;
  stt_model?: string | null;
  workflowType?: string | null;
  workflowLabel?: string | null;
  feature?: string;
  created_at: string;
  userId?: string;
  userName?: string;
  metadata?: Record<string, unknown>;
}

export interface WorkspaceBoostUsageResponse {
  totalBoostsSpent: number;
  totalProviderCostUsd: number;
  transactionCount: number;
  workspaceBalance: number;
  operations: BoostOperationBreakdown[];
  workflows: BoostWorkflowBreakdown[];
  workflowsUsed?: BoostWorkflowUsed[];
}

export interface WorkspaceBoostTransactionsResponse {
  transactions: BoostTransaction[];
  hasMore: boolean;
}

export interface WorkspaceBoostSummary {
  workspaceId: string;
  workspaceName: string | null;
  workspaceHandle: string | null;
  balance: number;
  totalBoostsSpent: number;
  totalProviderCostUsd: number;
  transactionCount: number;
}

export interface AllWorkspacesBoostSummaryResponse {
  workspaces: WorkspaceBoostSummary[];
  periodDays: number;
}

export async function adminGetWorkspaceBoostUsage(
  workspaceId: string,
  filter: AdminWorkflowFilter = {}
): Promise<WorkspaceBoostUsageResponse> {
  return callFunction("adminGetWorkspaceBoostUsage", {
    workspaceId,
    ...(filter.workflowType && { workflowType: filter.workflowType }),
    ...(filter.transcriptionWorkflow && { transcriptionWorkflow: filter.transcriptionWorkflow }),
  });
}

export interface AdminWorkflowFilter {
  workflowType?: string;
  transcriptionWorkflow?: string;
}

export interface AdminBoostTransactionFilters extends AdminWorkflowFilter {
  type?: string;
  userId?: string;
}

export async function adminGetWorkspaceBoostTransactions(
  workspaceId: string,
  limit?: number,
  offset?: number,
  startDate?: string,
  endDate?: string,
  filters: AdminBoostTransactionFilters = {}
): Promise<WorkspaceBoostTransactionsResponse> {
  return callFunction("adminGetWorkspaceBoostTransactions", {
    workspaceId,
    ...(limit !== undefined && { limit }),
    ...(offset !== undefined && { offset }),
    ...(startDate !== undefined && { startDate }),
    ...(endDate !== undefined && { endDate }),
    // Drop unset filters so the callable sees only what the admin chose.
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined)),
  });
}

export async function adminGetAllWorkspacesBoostSummary(
  days?: number
): Promise<AllWorkspacesBoostSummaryResponse> {
  return callFunction("adminGetAllWorkspacesBoostSummary", {
    ...(days !== undefined && { days }),
  });
}

export interface WorkspaceActivity {
  workspaceId: string;
  name: string | null;
  createdAt: string | null;
  isDev: boolean;
  billingHidden: boolean;
  recordings: number;
  transcriptions: number;
  lastTranscriptionAt: string | null;
  aiDocuments: number;
  lastAiDocumentAt: string | null;
  exports: number;
  lastExportAt: string | null;
  lastLoginAt: string | null;
  boostsBalance: number;
  boostsSpent: number;
  boostsPurchased: number;
  boostsGranted: number;
}

export interface WorkspaceActivityResponse {
  /** null = all time. Counts and Boost sums follow it; the last* dates are all time. */
  days: number | null;
  generatedAt: string;
  workspaces: WorkspaceActivity[];
}

export async function adminGetWorkspaceActivity(days?: number): Promise<WorkspaceActivityResponse> {
  return callFunction("adminGetWorkspaceActivity", {
    ...(days !== undefined && { days }),
  });
}

// ── Admin Costs by Operation ──────────────────────────────────────

export interface OperationCost {
  service: string;
  processor: string;
  totalProviderCostUsd: number;
  totalBoostsCharged: number;
  transactionCount: number;
  avgBoostsPerTxn: number;
  medianBoostsPerTxn: number;
  totalPromptTokens: number;
  totalOutputTokens: number;
  totalAudioDurationSec: number;
  totalPages: number;
}

export interface CostsByOperationResponse {
  periodDays: number;
  operations: OperationCost[];
  totals: {
    totalProviderCostUsd: number;
    totalBoostsCharged: number;
    transactionCount: number;
  };
}

export async function adminGetCostsByOperation(
  days?: number
): Promise<CostsByOperationResponse> {
  return callFunction("adminGetCostsByOperation", {
    ...(days !== undefined && { days }),
  });
}

// ── Admin Costs by Workflow ──────────────────────────────────────

export interface CostsByWorkflowResponse {
  periodDays: number;
  workflows: BoostWorkflowBreakdown[];
  totals: {
    totalBoostsCharged: number;
    totalProviderCostUsd: number;
    workflowCount: number;
  };
}

export async function adminGetCostsByWorkflow(
  workspaceId: string,
  days?: number
): Promise<CostsByWorkflowResponse> {
  return callFunction("adminGetCostsByWorkflow", {
    workspaceId,
    ...(days !== undefined && { days }),
  });
}

// ── Admin Pricing Config ──────────────────────────────────────────

export interface PricingConfigEntry {
  operationKey: string;
  providerUsdPerUnit: number | null;
  boostsPerUnit: number | null;
  category: string;
  unit: string;
}

export interface PricingConfigResponse {
  configs: PricingConfigEntry[];
  defaults: {
    documentAiProvider: { usd_per_unit: number };
  };
}

export async function adminGetPricingConfig(): Promise<PricingConfigResponse> {
  return callFunction<Record<string, never>, PricingConfigResponse>(
    "adminGetPricingConfig",
    {}
  );
}

export interface AdminGrantBoostsResponse {
  newBalance: number;
}

// =====================================================
// Workspace Boost History (User-facing)
// =====================================================

export interface WorkspaceBoostHistoryTransaction {
  id: string;
  type: string;
  user_name: string;
  feature: string;
  amount: number;
  description?: string;
  created_at: string;
  stt_model: string | null;
}

export interface WorkspaceBoostHistoryResponse {
  transactions: WorkspaceBoostHistoryTransaction[];
  hasMore: boolean;
}

export interface WorkspaceBoostHistoryParams {
  limit?: number;
  offset?: number;
  startDate?: string;
  endDate?: string;
  type?: string;
  userId?: string;
  sttModel?: string;
}

export async function getWorkspaceBoostHistory(
  workspaceId: string,
  params: WorkspaceBoostHistoryParams = {}
): Promise<WorkspaceBoostHistoryResponse> {
  // Drop unset filters so the callable sees only what the user chose.
  const defined = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined));
  return callFunction("getWorkspaceBoostHistory", { workspaceId, ...defined });
}

export async function adminGrantBoosts(
  workspaceId: string,
  amount: number,
  reason: string
): Promise<AdminGrantBoostsResponse> {
  // The grant is keyed on this, so a retried delivery of one call credits once.
  const idempotencyKey = crypto.randomUUID();
  return callFunction("adminGrantBoosts", { workspaceId, amount, reason, idempotencyKey });
}

// ── Admin Contract (hidden billing + monthly top-up) ─────────────

export interface WorkspaceContract {
  billingHidden: boolean;
  topUpEnabled: boolean;
  topUpAmount: number | null;
  /** YYYY-MM-DD; its day-of-month is the monthly anchor (America/Toronto). */
  startDate: string | null;
  expireRemaining: boolean;
  nextTopUpAt: string | null;
}

export type WorkspaceContractUpdate = Omit<WorkspaceContract, "nextTopUpAt">;

export interface UpdateWorkspaceContractResponse {
  settings: WorkspaceContract;
  /** True when saving applied a top-up at once. */
  applied: boolean;
}

export async function adminGetWorkspaceContract(workspaceId: string): Promise<WorkspaceContract> {
  return callFunction("adminGetWorkspaceContract", { workspaceId });
}

export async function adminUpdateWorkspaceContract(
  workspaceId: string,
  contract: WorkspaceContractUpdate
): Promise<UpdateWorkspaceContractResponse> {
  return callFunction("adminUpdateWorkspaceContract", { workspaceId, ...contract });
}

// =====================================================
// Admin Boost Gate Config (NaraFlow workflow gates)
// =====================================================

export interface WorkflowGateEntry {
  workflowType: string;
  minimumBoosts: number;
  coverCharge: number;
  minimumCost: number;
  description: string;
}

export interface BoostGateConfigResponse {
  workflows: WorkflowGateEntry[];
}

export async function adminGetBoostGateConfig(): Promise<BoostGateConfigResponse> {
  return callFunction<Record<string, never>, BoostGateConfigResponse>(
    "adminGetBoostGateConfig",
    {}
  );
}

export async function adminUpdateBoostGateConfig(
  workflows: { workflowType: string; minimumBoosts: number; coverCharge: number; minimumCost: number }[]
): Promise<BoostGateConfigResponse> {
  return callFunction("adminUpdateBoostGateConfig", { workflows });
}

// =====================================================
// Admin Stripe Product & Price Management
// =====================================================

export interface AdminStripePrice {
  id: string;
  unitAmount: number | null;
  currency: string;
  type: string;
  active: boolean;
  boosts: number | null;
  interval?: string;
  showOnPricing: boolean;
  displayName: string | null;
  displayDescription: string | null;
  popular: boolean;
  sortOrder: number | null;
}

export interface AdminStripeProduct {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  defaultPriceId: string | null;
  prices: AdminStripePrice[];
}

export interface AdminStripeProductsResponse {
  products: AdminStripeProduct[];
}

export interface AdminUpdateStripeProductResponse {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
}

export interface AdminUpdateStripePriceResponse {
  id: string;
  active: boolean;
  boosts: number | null;
  showOnPricing: boolean;
  displayName: string | null;
  displayDescription: string | null;
  popular: boolean;
  sortOrder: number | null;
}

export interface AdminCreateStripeProductResponse {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
}

export interface AdminCreateStripePriceResponse {
  id: string;
  unitAmount: number | null;
  currency: string;
  type: string;
  active: boolean;
  boosts: number | null;
  interval?: string;
}

export async function adminListStripeProducts(): Promise<AdminStripeProductsResponse> {
  return callFunction("adminListStripeProducts", {});
}

export async function adminUpdateStripeProduct(
  productId: string,
  updates: { name?: string; description?: string; active?: boolean }
): Promise<AdminUpdateStripeProductResponse> {
  return callFunction("adminUpdateStripeProduct", { productId, ...updates });
}

export async function adminUpdateStripePrice(
  priceId: string,
  updates: {
    active?: boolean;
    boosts?: number;
    showOnPricing?: boolean;
    displayName?: string;
    displayDescription?: string;
    popular?: boolean;
    sortOrder?: number;
  }
): Promise<AdminUpdateStripePriceResponse> {
  return callFunction("adminUpdateStripePrice", { priceId, ...updates });
}

export async function adminCreateStripeProduct(
  name: string,
  description?: string
): Promise<AdminCreateStripeProductResponse> {
  return callFunction("adminCreateStripeProduct", {
    name,
    ...(description !== undefined && { description }),
  });
}

export async function adminCreateStripePrice(
  productId: string,
  unitAmount: number,
  currency: string,
  type: "one_time" | "recurring",
  boosts: number,
  interval?: "month" | "year"
): Promise<AdminCreateStripePriceResponse> {
  return callFunction("adminCreateStripePrice", {
    productId,
    unitAmount,
    currency,
    type,
    boosts,
    ...(interval && { interval }),
  });
}

// =====================================================
// Public Pricing Plans
// =====================================================

export interface PricingPlan {
  priceId: string;
  name: string;
  description: string;
  type: "one_time" | "recurring";
  interval?: string;
  unitAmount: number;
  currency: string;
  boosts: number;
  popular: boolean;
  features: string[];
}

export interface GetPricingPlansResponse {
  subscriptions: PricingPlan[];
  boostPacks: PricingPlan[];
}

export async function getPricingPlans(): Promise<GetPricingPlansResponse> {
  return callFunction("getPricingPlans", {});
}

// =====================================================
// Admin User Management
// =====================================================

export interface AdminUser {
  id: string;
  email?: string;
  phone?: string;
  first_name?: string;
  last_name?: string;
  avatar_url?: string;
  locale?: string;
  timezone?: string;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  workspace_count?: number;
}

export interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
}

export async function adminGetAllUsers(
  skip = 0,
  limit = 20,
  search?: string
): Promise<AdminUsersResponse> {
  return callFunction("adminGetAllUsers", { skip, limit, search });
}

export async function adminGetUserDetail(userId: string): Promise<AdminUser> {
  return callFunction("adminGetUserDetail", { userId });
}

export async function adminUpdateUser(
  userId: string,
  data: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
    locale?: string;
    timezone?: string;
  }
): Promise<AdminUser> {
  return callFunction("adminUpdateUser", { userId, ...data });
}

// Membership summary for one workspace the user belongs to
export interface AdminUserWorkspace {
  id: string;
  name: string;
  handle?: string;
  role: WorkspaceCollaboratorRole | null;
  member_count: number;
}

export interface AdminUserWorkspacesResponse {
  workspaces: AdminUserWorkspace[];
  total: number;
}

/**
 * List the workspaces a given user belongs to (admin only).
 * Requires the parallel BFF function `adminGetUserWorkspaces`.
 */
export async function adminGetUserWorkspaces(
  userId: string
): Promise<AdminUserWorkspacesResponse> {
  return callFunction("adminGetUserWorkspaces", { userId });
}

export interface AdminPurgeUserStepResult {
  ok: boolean;
  detail?: string;
}

export interface AdminPurgeUserResponse {
  ok: boolean;
  userId: string;
  workspaceId: string | null;
  steps: {
    naraCorePurge?: AdminPurgeUserStepResult;
    gcsFiles?: AdminPurgeUserStepResult;
    stripeCustomer?: AdminPurgeUserStepResult;
    firestoreBoosts?: AdminPurgeUserStepResult;
    firebaseAuth?: AdminPurgeUserStepResult;
    [key: string]: AdminPurgeUserStepResult | undefined;
  };
}

/**
 * Permanently delete a user and their sole workspace (admin only).
 * Requires the parallel BFF function `adminPurgeUser`.
 */
export async function adminPurgeUser(data: {
  userId: string;
  workspaceId?: string | null;
  confirmEmail: string;
}): Promise<AdminPurgeUserResponse> {
  return callFunction("adminPurgeUser", data);
}

// =====================================================
// Trial Admin
// =====================================================

export interface AdminTrial {
  device_key: string;
  workspace_id: string;
  project_id: string;
  created_at: string;
  expires_at: string;
  reaped_at: string | null;
  workspace_name: string | null;
  handle: string | null;
  recording_count: number;
  boost_balance: number | null;
}

export interface AdminListTrialsResponse {
  trials: AdminTrial[];
}

export interface AdminResetTrialResponse {
  reset: boolean;
  workspace_id: string;
  deleted_counts: Record<string, number>;
}

/**
 * List all trials (admin only).
 * Requires the parallel BFF function `adminListTrials`.
 */
export async function adminListTrials(): Promise<AdminListTrialsResponse> {
  return callFunction("adminListTrials");
}

/**
 * Hard-delete one trial so its device can trial again (admin only).
 * Requires the parallel BFF function `adminResetTrial`.
 */
export async function adminResetTrial(workspaceId: string): Promise<AdminResetTrialResponse> {
  return callFunction("adminResetTrial", { workspaceId });
}

// =====================================================
// Transcription workflows (T-210, admin only)
// =====================================================

export interface WorkflowEntry {
  id: string;
  label: string;
  stt_model: string;
  status: "ga" | "preview";
  description: string;
}

export interface WorkspaceWorkflow {
  transcription_workflow: string;
  source: "workspace" | "default";
}

export async function adminListWorkflows(): Promise<{ workflows: WorkflowEntry[] }> {
  return callFunction("adminListWorkflows");
}

export async function adminGetWorkspaceWorkflow(workspaceId: string): Promise<WorkspaceWorkflow> {
  return callFunction("adminGetWorkspaceWorkflow", { workspaceId });
}

export async function adminSetWorkspaceWorkflow(workspaceId: string, workflow: string): Promise<WorkspaceWorkflow> {
  return callFunction("adminSetWorkspaceWorkflow", { workspaceId, workflow });
}

// =====================================================
// Workspace Status
// =====================================================

export async function getWorkspaceVertexIndexStatus(workspaceId: string): Promise<VertexIndexStatusResponse> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/index`);
}

export async function triggerWorkspaceReindex(workspaceId: string): Promise<{
  status: string;
  workspace_id: string;
  total_documents: number;
  indexed: number;
  failed: number;
}> {
  return callRoute("POST", `/workspaces/${seg(workspaceId)}/index/reindex`);
}

// =====================================================
// NaraChef (Recipe Editor Agent)
// =====================================================

export interface ChefChatResponse {
  success: boolean;
  response: string;
  session_id: string;
  error?: string;
  choices?: string[];
  blocks?: Array<{ type: string; [key: string]: unknown }>;
}

export interface ChefSessionSummary {
  id: string;
  title?: string;
  recipe_id?: string;
  status: string;
  message_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChefSessionsListResponse {
  sessions: ChefSessionSummary[];
  count: number;
}

export interface ChefMessage {
  id: string;
  role: string;
  content: string;
  metadata?: Record<string, unknown>;
  created_at?: string;
}

export interface ChefSessionDetail {
  id: string;
  user_id: string;
  recipe_id?: string;
  title?: string;
  status: string;
  created_at: string;
  messages: ChefMessage[];
}

export async function chefChat(data: {
  message: string;
  sessionId?: string;
  recipeId?: string;
}): Promise<ChefChatResponse> {
  return callFunction("chefChat", data);
}

export async function listChefSessions(): Promise<ChefSessionsListResponse> {
  return callFunction("listChefSessions", {});
}

export async function getChefSession(sessionId: string): Promise<ChefSessionDetail> {
  return callFunction("getChefSession", { sessionId });
}

export async function deleteChefSession(sessionId: string): Promise<{ status: string }> {
  return callFunction("deleteChefSession", { sessionId });
}

// --- Direct recipe operations (bypass AI agent) ---

export interface ChefRecipeSummary {
  id: string;
  name: string;
  input_mode: string;
  status: string;
  description?: string;
  active_version?: number;
  has_draft_changes?: boolean;
}

export interface ChefListRecipesResponse {
  recipes: ChefRecipeSummary[];
  count: number;
}

export async function chefListRecipes(inputMode?: string): Promise<ChefListRecipesResponse> {
  return callFunction("chefListRecipes", { input_mode: inputMode });
}

/** Every recipe (the list is global), for a member of the workspace: the Generate document page. */
export async function listWorkspaceRecipes(workspaceId: string, inputMode?: string): Promise<ChefListRecipesResponse> {
  return callRoute("GET", `/workspaces/${seg(workspaceId)}/recipes${inputMode ? `?input_mode=${seg(inputMode)}` : ""}`);
}

export async function chefGetRecipe(recipeId: string): Promise<any> {
  return callFunction("chefGetRecipe", { recipeId });
}

export async function renderHtmlPreview(
  recipeId: string,
  storyPayload?: Record<string, unknown>,
  files?: Array<Record<string, unknown>>,
  useDraft?: boolean
): Promise<{ success: boolean; html?: string; error?: string }> {
  return callFunction("renderHtmlPreview", {
    recipeId,
    ...(storyPayload && { storyPayload }),
    ...(files && files.length > 0 && { files }),
    ...(useDraft !== undefined && { useDraft }),
  });
}

// --- Admin config ---

export interface ChefConfigItem {
  key: string;
  value: string;
  updated_at: string | null;
}

export async function getChefConfig(): Promise<{ configs: ChefConfigItem[] }> {
  return callFunction("getChefConfig", {});
}

export async function updateChefConfig(
  configs: Record<string, string>
): Promise<{ success: boolean; updated_keys: string[] }> {
  return callFunction("updateChefConfig", { configs });
}

// =====================================================
// Admin Settings
// =====================================================

export interface WizardInviteTemplates {
  smsTemplate: string;
  emailSubject: string;
  emailTemplate: string;
}

export interface AdminSettings {
  wizardInviteTemplates: WizardInviteTemplates;
}

export async function adminGetSettings(): Promise<AdminSettings> {
  return callFunction("adminGetSettings", {});
}

export async function adminUpdateSettings(
  settings: Partial<AdminSettings>
): Promise<{ success: boolean }> {
  return callFunction("adminUpdateSettings", settings);
}

// App Settings (NaraWorkspace global settings)
export interface AppSetting {
  key: string;
  value: unknown;
  description?: string;
  updated_at?: string;
}

export async function adminGetAppSettings(): Promise<AppSetting[]> {
  return callFunction("adminGetAppSettings", {});
}

export async function adminUpdateAppSetting(
  key: string,
  value: unknown
): Promise<{ success: boolean }> {
  return callFunction("adminUpdateAppSetting", { key, value });
}

// =====================================================
// Send Wizard Invite
// =====================================================

export async function sendWizardInvite(data: {
  recipientUserId: string;
  channel: "email" | "sms";
  message: string;
  wizardLink: string;
  recipientEmail?: string;
  recipientPhone?: string;
}): Promise<{ success: boolean; channel: string }> {
  return callFunction("sendWizardInvite", data);
}

// =====================================================
// Admin API Key Management
// =====================================================

export interface AdminApiKey {
  id: string;
  name: string;
  key_prefix: string;
  allowed_ips: string[];
  active: boolean;
  created_at: string | null;
  created_by: string;
  last_used_at: string | null;
}

export async function adminListApiKeys(): Promise<{ keys: AdminApiKey[] }> {
  return callFunction("adminListApiKeys", {});
}

export async function adminCreateApiKey(data: {
  name: string;
  allowed_ips: string[];
}): Promise<{
  key: string;
  name: string;
  key_prefix: string;
  allowed_ips: string[];
}> {
  return callFunction("adminCreateApiKey", data);
}

export async function adminRevokeApiKey(
  id: string
): Promise<{ success: boolean }> {
  return callFunction("adminRevokeApiKey", { id });
}

export async function adminUpdateApiKey(
  id: string,
  data: { name?: string; allowed_ips?: string[] }
): Promise<{ success: boolean }> {
  return callFunction("adminUpdateApiKey", { id, ...data });
}

// =====================================================
// Server-Driven Wizard Types
// =====================================================

export interface WizardBlock {
  block_id: string;
  label: string;
  category: string;
  ui_type: string;
  activity: string | null;
  default_config: Record<string, any>;
  required_fields: string[];
  produced_data: string[];
}

export interface WizardTemplateStep {
  id: string;
  block_id: string;
  sort_order: number;
  config_overrides: Record<string, any> | null;
  timeout_seconds: number | null;
  skippable: boolean;
}

export interface WizardTemplate {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  is_active: boolean;
  ui_config: Record<string, any> | null;
  steps: WizardTemplateStep[];
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  archived_at: string | null;
  instance_count?: number;
}

export type WizardStepStatus = 'pending' | 'waiting' | 'executing' | 'completed' | 'skipped' | 'failed';
export type WizardInstanceStatus = 'in_progress' | 'completed' | 'abandoned' | 'timed_out';

// Wizard Instance Summary (for list views)
export interface WizardInstanceSummary {
  instance_id: string;
  template_id: string;
  template_name: string;
  user_id: string;
  user_display_name?: string;
  workspace_id: string;
  status: string;
  current_step_index: number;
  current_step_id: string | null;
  started_at: string | null;
  completed_at: string | null;
  updated_at: string | null;
  total_xp?: number;
}

export interface WizardInstanceDetail extends WizardInstanceSummary {
  steps: Array<{
    step_id: string;
    block_id: string;
    status: string;
    data: Record<string, any>;
    action_result?: any;
    error?: string;
    completed_at?: string;
  }>;
  collected_data: Record<string, any>;
  template_snapshot: Record<string, any>;
  ui_config: Record<string, any> | null;
}

export interface StepState {
  step_id: string;
  block_id: string;
  status: WizardStepStatus;
  data: Record<string, any>;
  action_result: Record<string, any> | null;
  error: string | null;
  completed_at: string | null;
}

export interface WizardState {
  instance_id: string;
  template_name: string;
  user_id: string;
  workspace_id: string | null;
  status: WizardInstanceStatus;
  current_step_id: string | null;
  current_step_index: number;
  steps: StepState[];
  collected_data: Record<string, any>;
  ui_config: Record<string, any> | null;
}

// =====================================================
// Server-Driven Wizard API
// =====================================================

export async function listWizardBlocks(): Promise<{ blocks: WizardBlock[] }> {
  const blocks = (await callFunction("listWizardBlocks", {})) as WizardBlock[];
  return { blocks };
}

export async function listWizardTemplates(): Promise<WizardTemplate[]> {
  return callFunction("listWizardTemplates", {});
}

export async function getWizardTemplate(id: string): Promise<WizardTemplate> {
  return callFunction("getWizardTemplate", { templateId: id });
}

export async function createWizardTemplate(data: {
  name: string;
  slug: string;
  description?: string;
  is_active?: boolean;
  ui_config?: Record<string, any>;
  steps?: Array<{
    block_id: string;
    sort_order: number;
    config_overrides?: Record<string, any>;
    timeout_seconds?: number;
    skippable?: boolean;
  }>;
}): Promise<WizardTemplate> {
  return callFunction("createWizardTemplate", data);
}

export async function updateWizardTemplate(
  id: string,
  data: {
    name?: string;
    slug?: string;
    description?: string;
    is_active?: boolean;
    ui_config?: Record<string, any>;
    closed_at?: string | null;
    archived_at?: string | null;
    steps?: Array<{
      block_id: string;
      sort_order: number;
      config_overrides?: Record<string, any>;
      timeout_seconds?: number;
      skippable?: boolean;
    }>;
  }
): Promise<WizardTemplate> {
  return callFunction("updateWizardTemplate", { templateId: id, ...data });
}

// The wizard routes are the signed-in user's own (no workspace).
const wizardInstancePath = (instanceId: string) => `/wizard/instances/${seg(instanceId)}`;

export async function getWizardState(instanceId: string): Promise<WizardState> {
  return callRoute("GET", wizardInstancePath(instanceId));
}

export async function completeWizardStep(
  instanceId: string,
  stepId: string,
  data: Record<string, any>
): Promise<WizardState> {
  return callRoute("POST", `${wizardInstancePath(instanceId)}/complete-step`, { stepId, data });
}

export async function createWizardInstance(
  templateId: string,
  userId: string
): Promise<WizardState> {
  return callFunction("createWizardInstance", { templateId, userId });
}

export async function listWizardInstances(params: {
  templateId?: string;
  userId?: string;
  statusFilter?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: WizardInstanceSummary[]; total: number }> {
  return callFunction("listWizardInstances", params);
}

export async function getWizardInstanceDetail(instanceId: string): Promise<WizardInstanceDetail> {
  return callFunction("getWizardInstanceDetail", { instanceId });
}

export async function deleteWizardInstance(instanceId: string): Promise<{ deleted: boolean; count: number }> {
  return callFunction("deleteWizardInstance", { instanceId });
}

export async function bulkDeleteWizardInstances(instanceIds: string[]): Promise<{ deleted: boolean; count: number }> {
  return callFunction("bulkDeleteWizardInstances", { instanceIds });
}

export async function provisionOnboardingWizard(): Promise<{ instance_id: string; is_new: boolean }> {
  return provisionWizardBySlug("onboarding");
}

export async function provisionWizardBySlug(slug: string): Promise<{ instance_id: string; is_new: boolean }> {
  return callRoute("POST", "/wizard/instances", { slug });
}

// =====================================================
// User XP
// =====================================================

export interface UserXpInstance {
  instance_id: string;
  template_name: string;
  xp: number;
  status: string;
  completed_at: string | null;
}

export interface UserXpResponse {
  user_id: string;
  total_xp: number;
  instances: UserXpInstance[];
}

export async function getUserXp(): Promise<UserXpResponse> {
  return callRoute("GET", "/wizard/xp");
}

// =====================================================
// Admin Infrastructure
// =====================================================

export interface InfrastructureNodeRuntime {
  revision?: string;
  memory?: string;
  cpu?: string;
  maxInstances?: number;
  lastDeploy?: string;
  serviceAccount?: string;
  url?: string;
  status?: string;
}

export interface InfrastructureSubService {
  name: string;
  description: string;
  capabilities: string[];
}

export interface InfrastructureNode {
  id: string;
  label: string;
  description?: string;
  subServices?: InfrastructureSubService[];
  layer: string;
  icon: string;
  runtime?: InfrastructureNodeRuntime;
}

export interface InfrastructureEdge {
  source: string;
  target: string;
  type: string;
}

export interface InfrastructureData {
  nodes: InfrastructureNode[];
  edges: InfrastructureEdge[];
  lastRefreshed: string | null;
}

export async function adminGetInfrastructure(): Promise<InfrastructureData> {
  return callFunction<Record<string, unknown>, InfrastructureData>("adminGetInfrastructure");
}

export async function adminRefreshInfrastructure(): Promise<InfrastructureData> {
  return callFunction<Record<string, unknown>, InfrastructureData>("adminRefreshInfrastructure");
}
